'use client';

import React, { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { ComparisonSection } from 'components/ring-pro';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { ButtonV2 } from 'components/shared/ButtonV2';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { getRingProBasePrice } from 'content/products/ring-pro/tierPricing';
import {
  RingPriceData,
  AirRingColors,
} from 'content/products/ring/productMap/constants';
import { getCurrencySymbolFromEnum } from 'lib/region/countryUtils';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { formatPriceWithComma } from 'hooks/pricing/useFormattedPrice';
import styles from './pro-vs-air-client.module.css';

const ProVsAirPage = () => {
  const { store, currency } = useCartStore(
    useShallow((s) => ({ store: s.store, currency: s.currency })),
  );
  const { region } = useRegionLocale();
  const t = useTranslations('common');
  const isRingAirUnavailable =
    region?.toUpperCase() === 'US' || region?.toUpperCase() === 'PR';

  const { airPrice, proPrice } = useMemo(() => {
    const s = store ?? ShopifyStore.ROW;
    const c = (currency?.toLowerCase() as Currency) ?? Currency.usd;
    const symbol = getCurrencySymbolFromEnum(c);
    const airBase =
      RingPriceData[s]?.[AirRingColors.RAW_TITANIUM]?.price ?? 349;
    const proBase = getRingProBasePrice(s, c) ?? 479;
    return {
      airPrice: `${symbol}${formatPriceWithComma(airBase, c)}`,
      proPrice: `${symbol}${formatPriceWithComma(proBase, c)}`,
    };
  }, [store, currency]);

  return (
    <main>
      <section className={styles.heroSection}>
        <h1 className={styles.heroTitle}>Compare PRO vs AIR</h1>
        <p className={styles.heroSubtitle}>
          See how Ring PRO and Ring AIR stack up side by side
        </p>
      </section>

      <ComparisonSection hideTitle />

      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <div className={styles.ctaCard}>
            <div className={styles.ctaCardImage}>
              <CustomImage
                src="web_v2/ring-pro/comparison/ring-pro-thumb.png"
                width={80}
                height={80}
                alt="Ultrahuman Ring PRO"
                style={{ width: '100%', height: '100%' }}
              />
            </div>
            <p className={styles.ctaCardTitle}>Ultrahuman Ring PRO</p>
            <p className={styles.ctaCardPrice}>{proPrice}</p>
            <ButtonV2 href="/ring-pro/buy" variant="blue">
              Buy now
            </ButtonV2>
          </div>
          <div className={styles.ctaCard}>
            <div className={styles.ctaCardImage}>
              <CustomImage
                src="web_v2/ring-pro/comparison/ring-air-thumb.png"
                width={80}
                height={80}
                alt="Ultrahuman Ring AIR"
                style={{ width: '100%', height: '100%' }}
              />
            </div>
            <p className={styles.ctaCardTitle}>Ultrahuman Ring AIR</p>
            <p className={styles.ctaCardPrice}>{airPrice}</p>
            {isRingAirUnavailable ? (
              <p className={styles.ctaUnavailable}>
                {t('productSelectionSection.content.currentlyUnavailable')}
              </p>
            ) : (
              <ButtonV2 href="/ring/buy" variant="blue">
                Buy now
              </ButtonV2>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProVsAirPage;
